
import React from 'react';
import { Client, SocialProfiles } from '../types'; // Make sure SocialProfiles is imported
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { EnrichedData, MappableClientField } from './enrichmentTypes';
import { Badge } from '@/components/ui/badge';

interface EnrichmentResultsDisplayProps {
  client: Client;
  actualCompanyData: EnrichedData;
  selectedFields: Record<keyof Client, boolean>;
  handleToggleField: (fieldKey: keyof Client) => void;
  mappableFields: Array<MappableClientField<keyof Client>>;
}

const getEnrichedValueFromData = (data: EnrichedData | null, enrichedKey: string | string[]): unknown => {
  if (!data) return undefined;
  if (Array.isArray(enrichedKey)) {
    for (const key of enrichedKey) {
      if (data[key] !== undefined) return data[key];
    }
    return undefined;
  }
  return data[enrichedKey];
};

const formatDisplayValue = (value: unknown): string | React.ReactNode => {
  if (value === null || value === undefined) return 'N/A';
  
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) { // For technologies_used
      if (value.length === 0) return 'N/A';
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => <Badge key={index} variant="secondary">{String(item)}</Badge>)}
        </div>
      );
    }
    // For social_profiles - safely handle object types
    const objectValue = value as Record<string, unknown>;
    const socialKeys = Object.keys(objectValue);
    if (socialKeys.length === 0) return 'N/A';
    return socialKeys.map(key => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${String(objectValue[key])}`).join(', ');
  }
  
  return String(value);
};

export const EnrichmentResultsDisplay: React.FC<EnrichmentResultsDisplayProps> = ({
  client,
  actualCompanyData,
  selectedFields,
  handleToggleField,
  mappableFields,
}) => {
  return (
    <div>
      <h3 className="text-lg font-medium mb-2">Apply Enriched Data</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Select fields to update. Current values are shown for comparison.
      </p>
      <div className="space-y-3">
        {mappableFields.map(field => {
          const enrichedValueRaw = getEnrichedValueFromData(actualCompanyData, field.enrichedKey);
          
          // Handle transformation safely
          let transformedValue: unknown = enrichedValueRaw;
          if (field.transform && enrichedValueRaw !== undefined) {
            transformedValue = field.transform(enrichedValueRaw);
          }
          
          // Format the final display value (this returns string | ReactNode)
          const enrichedValueDisplay = formatDisplayValue(transformedValue);
          const currentValue = formatDisplayValue(client[field.key]);
          
          // Only render if there's a meaningful enriched value (after potential transformation)
          // Check raw value first, then check if transformed value is non-empty or not N/A
          let shouldRender = false;
          if (enrichedValueRaw !== undefined && enrichedValueRaw !== null) {
             if (typeof enrichedValueRaw === 'string' && enrichedValueRaw.trim() === "") {
                shouldRender = false;
             } else if (Array.isArray(enrichedValueRaw) && enrichedValueRaw.length === 0){
                shouldRender = false;
             } else if (typeof enrichedValueRaw === 'object' && !Array.isArray(enrichedValueRaw) && Object.keys(enrichedValueRaw as Record<string, unknown>).length === 0) {
                shouldRender = false;
             }
             else {
                shouldRender = true;
             }
          }

          if (!shouldRender) return null;

          return (
            <div key={field.key as string} className="flex items-start justify-between p-3 border rounded-md">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id={`client-field-${String(field.key)}`}
                  checked={!!selectedFields[field.key]}
                  onCheckedChange={() => handleToggleField(field.key)}
                  className="mt-1"
                />
                <Label htmlFor={`client-field-${String(field.key)}`} className="text-sm font-medium">
                  {field.label}
                </Label>
              </div>
              <div className="text-right ml-4 flex-shrink-0" style={{maxWidth: '50%'}}>
                <div className="text-xs text-muted-foreground truncate" title={typeof currentValue === 'string' ? currentValue : 'Current complex value'}>Current: {currentValue}</div>
                <div className="text-sm text-primary truncate" title={typeof enrichedValueDisplay === 'string' ? enrichedValueDisplay : 'New complex value'}>New: {enrichedValueDisplay}</div>
              </div>
            </div>
          );
        })}
        {/* Removed special handling for notes (description/overview) as description is now a mappable field */}
      </div>
    </div>
  );
};
